import { useCallback, useMemo } from 'react';
import { SiteviewComponent, type TTooltipEvent } from '@accure/siteview';

import { AssetType } from '@/siteview/entities/asset-type';
import { useAssetTooltip } from '@/siteview/hooks/use-asset-tooltip';
import { useViewFilters } from '@/siteview/hooks/use-view-filters';

import { useCurrentAssets } from '../../hooks/use-current-assets';
import { useCurrentStaticData } from '../../hooks/use-current-static-data';
import { useSettings } from '../../hooks/use-settings';

import { getSiteviewDynamicData } from './get-siteview-dynamic-data';
import { getSiteviewSettings } from './get-siteview-settings';

export type MapProps = {};

export function Map(_: MapProps) {
  const [, setTooltip] = useAssetTooltip();
  const [viewFilters] = useViewFilters();
  const [settings] = useSettings();
  const [staticData] = useCurrentStaticData();
  const [{ assets, assetsFilters }] = useCurrentAssets();

  const dynamicData = useMemo(
    () =>
      getSiteviewDynamicData(
        assets,
        staticData,
        assetsFilters,
        viewFilters,
        settings,
      ),
    [assets, staticData, assetsFilters, viewFilters, settings],
  );

  const siteviewSettings = useMemo(
    () => getSiteviewSettings(assetsFilters, viewFilters, settings),
    [assetsFilters, viewFilters, settings],
  );

  const onHover = useCallback(
    function (event: TTooltipEvent | null) {
      setTooltip(function (tooltip) {
        if (tooltip?.static) {
          // The tooltip is static, only a click outside can change it
          return tooltip;
        }

        if (!event) {
          return undefined;
        }

        return {
          id: event.elementUid,
          type: AssetType.CONTAINER, // FIXME: should come from event
          static: false,
          pos: {
            x: event.position.x,
            y: event.position.y,
          },
        };
      });
    },
    [setTooltip],
  );

  const onClick = useCallback(
    function (event: TTooltipEvent | null) {
      setTooltip(
        event
          ? {
              id: event.elementUid,
              type: AssetType.CONTAINER, // FIXME: should come from event
              static: true,
              pos: {
                x: event.position.x,
                y: event.position.y,
              },
            }
          : undefined,
      );
    },
    [setTooltip],
  );

  return (
    <SiteviewComponent
      // @ts-expect-error type doesn't match for now, it's expected
      staticData={staticData}
      dynamicData={dynamicData}
      settings={siteviewSettings}
      onClick={onClick}
      onHover={onHover}
    />
  );
}
